import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  FiHome,
  FiDollarSign,
  FiCheckCircle,
  FiClock,
  FiUser,
  FiCalendar,
  FiTrendingUp,
  FiTrendingDown,
  FiFileText,
  FiCreditCard,
  FiArrowUp,
  FiArrowDown,
  FiRefreshCw,
  FiBarChart2
} from 'react-icons/fi';
import {
  FaWallet,
  FaMoneyBillWave,
  FaChartLine,
  FaGem,
  FaStar,
  FaBuilding,
  FaHandHoldingUsd
} from 'react-icons/fa';
import { useSelector } from 'react-redux';

const Overview = () => {
  const [dashboardData, setDashboardData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const userData = useSelector((state) => state.user_info[0]?.user);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true);
        setError(null);

        if (!userData || !userData.properties || !userData.transactions) {
          throw new Error('User data is incomplete or missing');
        }

        const totalAmount = userData.properties.reduce(
          (sum, property) => sum + (property.value || 0),
          0
        );

        const paidAmount = userData.transactions
          .filter((t) => t.status === 'Success')
          .reduce((sum, transaction) => sum + (transaction.amount || 0), 0);

        setDashboardData({
          user: userData,
          totalAmount,
          paidAmount,
          remainingAmount: totalAmount - paidAmount,
          nextEmiDate: userData.properties[0]?.startDate || null,
          nextEmiAmount: userData.properties[0]?.emiAmount || 0,
        });
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
        setError('Failed to load dashboard data. Please try again later.');
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [userData]);

  const formatCurrency = (amount) => {
    if (!amount) return '₹0';
    if (amount >= 10000000) return `₹${(amount / 10000000).toFixed(2)} Cr`;
    if (amount >= 100000) return `₹${(amount / 100000).toFixed(2)} Lakh`;
    return `₹${amount.toLocaleString('en-IN')}`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  };

  const getStatusColor = (status) => {
    switch(status) {
      case 'Success': return 'bg-emerald-100 text-emerald-700 border-emerald-200';
      case 'Pending': return 'bg-amber-100 text-amber-700 border-amber-200';
      case 'Failed': return 'bg-red-100 text-red-700 border-red-200';
      default: return 'bg-gray-100 text-gray-700 border-gray-200';
    }
  };

  const getStatusIcon = (status) => {
    switch(status) {
      case 'Success': return <FiCheckCircle className="w-3 h-3" />;
      case 'Pending': return <FiClock className="w-3 h-3" />;
      case 'Failed': return <FiTrendingDown className="w-3 h-3" />;
      default: return null;
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-indigo-50/30 to-purple-50/30 flex items-center justify-center">
        <div className="text-center">
          <div className="relative">
            <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-indigo-500 mx-auto"></div>
            <div className="absolute inset-0 flex items-center justify-center">
              <FaGem className="w-6 h-6 text-indigo-400 animate-pulse" />
            </div>
          </div>
          <p className="mt-4 text-gray-500 font-medium">Loading your dashboard...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-indigo-50/30 to-purple-50/30 flex items-center justify-center">
        <div className="text-center bg-white rounded-2xl p-8 shadow-xl max-w-md">
          <div className="w-16 h-16 mx-auto mb-4 bg-red-100 rounded-full flex items-center justify-center">
            <FiHome className="w-8 h-8 text-red-500" />
          </div>
          <h3 className="text-xl font-semibold text-gray-800 mb-2">Error Loading Dashboard</h3>
          <p className="text-gray-500 mb-4">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  if (!dashboardData) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-indigo-50/30 to-purple-50/30 flex items-center justify-center">
        <div className="text-center bg-white rounded-2xl p-8 shadow-xl max-w-md">
          <div className="w-16 h-16 mx-auto mb-4 bg-gray-100 rounded-full flex items-center justify-center">
            <FiHome className="w-8 h-8 text-gray-400" />
          </div>
          <p className="text-gray-500 font-medium">No data available</p>
        </div>
      </div>
    );
  }

  const statsCards = [
    {
      id: 'total',
      title: 'Total Property Value',
      value: formatCurrency(dashboardData.totalAmount),
      subLabel: `${dashboardData.user.properties.length} properties`,
      icon: <FaBuilding className="w-6 h-6" />,
      bg: 'from-indigo-500 to-purple-500',
      borderColor: 'border-indigo-200',
      bgGradient: 'from-indigo-50 to-purple-50'
    },
    {
      id: 'paid',
      title: 'Amount Paid',
      value: formatCurrency(dashboardData.paidAmount),
      subLabel: `${((dashboardData.paidAmount / dashboardData.totalAmount) * 100 || 0).toFixed(1)}% of total`,
      icon: <FiCheckCircle className="w-6 h-6" />,
      bg: 'from-emerald-500 to-teal-500',
      borderColor: 'border-emerald-200',
      bgGradient: 'from-emerald-50 to-teal-50'
    },
    {
      id: 'remaining',
      title: 'Remaining Amount',
      value: formatCurrency(dashboardData.remainingAmount),
      subLabel: `${((dashboardData.remainingAmount / dashboardData.totalAmount) * 100 || 0).toFixed(1)}% remaining`,
      icon: <FaMoneyBillWave className="w-6 h-6" />,
      bg: 'from-amber-500 to-orange-500',
      borderColor: 'border-amber-200',
      bgGradient: 'from-amber-50 to-orange-50'
    },
    {
      id: 'emi',
      title: 'Next EMI Due',
      value: formatCurrency(dashboardData.nextEmiAmount),
      subLabel: dashboardData.nextEmiDate ? formatDate(dashboardData.nextEmiDate) : 'No EMI due',
      icon: <FiCalendar className="w-6 h-6" />,
      bg: 'from-blue-500 to-cyan-500',
      borderColor: 'border-blue-200',
      bgGradient: 'from-blue-50 to-cyan-50'
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-indigo-50/30 to-purple-50/30 py-6 sm:py-10">
      <div className="container mx-auto px-4 max-w-7xl">
        {/* Header */}
        <div className="mb-8">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <div className="flex items-center gap-2 mb-2">
                <div className="w-1 h-8 bg-gradient-to-b from-indigo-500 to-purple-500 rounded-full"></div>
                <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">
                  Welcome back!
                </h1>
              </div>
              <p className="text-gray-500 mt-1 text-sm">
                Hello, <span className="font-semibold text-indigo-600">{dashboardData.user.customerName}</span> • Here's your financial overview
              </p>
            </div>
            <div className="flex items-center gap-2">
              <span className="flex items-center gap-2 px-3 py-1.5 bg-emerald-100 rounded-full">
                <span className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse"></span>
                <span className="text-xs font-semibold text-emerald-700">Live Dashboard</span>
              </span>
            </div>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 mb-8">
          {statsCards.map((card, idx) => (
            <motion.div
              key={card.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: idx * 0.1 }}
              whileHover={{ y: -4 }}
              className={`bg-white rounded-2xl shadow-lg border ${card.borderColor} overflow-hidden hover:shadow-xl transition-all duration-300`}
            >
              <div className={`bg-gradient-to-r ${card.bgGradient} px-5 py-4 border-b ${card.borderColor}`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className={`p-2 rounded-xl bg-gradient-to-r ${card.bg} text-white shadow-md`}>
                      {card.icon}
                    </div>
                    <h3 className="font-semibold text-gray-800">{card.title}</h3>
                  </div>
                  <FaStar className="w-4 h-4 text-yellow-400" />
                </div>
              </div>
              <div className="p-5">
                <p className="text-2xl font-bold text-gray-800">{card.value}</p>
                <p className="text-xs text-gray-500 mt-1">{card.subLabel}</p>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Progress Bar */}
        <div className="bg-white rounded-2xl shadow-lg border border-gray-100 mb-8 overflow-hidden">
          <div className="px-6 py-4 bg-gradient-to-r from-indigo-50 to-purple-50 border-b border-gray-100">
            <div className="flex items-center gap-2">
              <FiTrendingUp className="w-5 h-5 text-indigo-600" />
              <h3 className="font-semibold text-gray-800">Payment Progress</h3>
            </div>
          </div>
          <div className="p-6">
            <div className="flex justify-between mb-3">
              <span className="text-sm text-gray-500">Paid</span>
              <span className="text-sm font-medium text-gray-700">
                {dashboardData.totalAmount > 0
                  ? ((dashboardData.paidAmount / dashboardData.totalAmount) * 100).toFixed(1)
                  : '0'}%
              </span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
              <motion.div
                initial={{ width: 0 }}
                animate={{
                  width: dashboardData.totalAmount > 0
                    ? `${(dashboardData.paidAmount / dashboardData.totalAmount) * 100}%`
                    : '0%'
                }}
                transition={{ duration: 1, delay: 0.5 }}
                className="bg-gradient-to-r from-indigo-500 to-purple-500 h-3 rounded-full"
              />
            </div>
            <div className="flex justify-between mt-3">
              <span className="text-xs text-gray-400">0%</span>
              <span className="text-xs text-gray-400">50%</span>
              <span className="text-xs text-gray-400">100%</span>
            </div>
          </div>
        </div>

        {/* Recent Transactions */}
        <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
          <div className="bg-gradient-to-r from-indigo-50 to-purple-50 px-6 py-4 border-b border-gray-100">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <FiFileText className="w-5 h-5 text-indigo-600" />
                <h3 className="font-semibold text-gray-800">Recent Transactions</h3>
              </div>
              <span className="text-xs bg-indigo-100 text-indigo-700 px-2 py-1 rounded-full">
                {dashboardData.user.paymentHistory?.length || 0} transactions
              </span>
            </div>
          </div>
          <div className="divide-y divide-gray-100">
            {dashboardData.user.paymentHistory?.length > 0 ? (
              dashboardData.user.paymentHistory.map((transaction, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className="px-6 py-4 hover:bg-gray-50 transition cursor-pointer"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className={`w-10 h-10 rounded-xl ${getStatusColor(transaction.status)} flex items-center justify-center`}>
                        {getStatusIcon(transaction.status)}
                      </div>
                      <div>
                        <p className="font-medium text-gray-800">{transaction.paymentMethod || 'Payment'}</p>
                        <div className="flex items-center gap-3 mt-1">
                          <span className="text-xs text-gray-500">{formatDate(transaction.date)}</span>
                          <span className="text-xs text-gray-500">•</span>
                          <span className="text-xs text-gray-500">{transaction.description || 'Transaction'}</span>
                        </div>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-bold text-gray-800">{formatCurrency(transaction.amount)}</p>
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold ${getStatusColor(transaction.status)}`}>
                        {getStatusIcon(transaction.status)}
                        {transaction.status}
                      </span>
                    </div>
                  </div>
                </motion.div>
              ))
            ) : (
              <div className="px-6 py-12 text-center">
                <div className="flex flex-col items-center justify-center">
                  <div className="w-16 h-16 mb-4 bg-gray-100 rounded-full flex items-center justify-center">
                    <FiFileText className="w-8 h-8 text-gray-400" />
                  </div>
                  <p className="text-gray-500 font-medium">No transactions found</p>
                  <p className="text-sm text-gray-400 mt-1">Your recent payments will appear here</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Overview;